/**
 * Public gallery authorization contracts (sharing redesign · Phase 6).
 *
 * FROZEN. The Worker resolver, public client, download routes, and Phase 7
 * Join resolver consume this module. They must not restate its denial codes,
 * endpoint matrix, tier vocabulary, session policy tag, or audit payloads.
 *
 * The functions here are deliberately pure. Secret verification, invitation
 * lookup, R2 existence checks, and HTTP response construction stay in the
 * Worker, which feeds their bounded outcomes into `resolveShareAccess`.
 */

import {
  SHARE_CLIENT_MODE_MATRIX,
  shareClientCapabilities,
  type OriginalCopy,
  type PreparedCopy,
  type ShareAccessMode,
  type ShareClientAbility,
  type ShareClientMode,
  type SharePolicyClientDto,
} from './contracts'

export const SHARE_ACCESS_CONTRACT_VERSION = 1 as const

export type SharePolicySource = 'versioned' | 'legacy'
export type LegacyDownloadMode = 'off' | 'web' | 'hr' | 'original'

/** The exact persisted Worker fields Phase 6 may read. */
export interface StoredSharePolicyProjection {
  policyVersion: number
  accessMode: ShareAccessMode
  passcodeRequired: boolean
  clientMode: string | null
  preparedFormat: string | null
  allowOriginals: boolean
  legacyPicksEnabled: boolean
  legacyDownloadEnabled: LegacyDownloadMode
  expiresAt: number
}

export type StoredSharePolicyAdapterResult =
  | { ok: true; source: SharePolicySource; dto: SharePolicyClientDto }
  | { ok: false; denial: 'unsupported_policy_version' | 'malformed_policy' }

const CLIENT_MODES: readonly ShareClientMode[] = ['view', 'select', 'approve']
const PREPARED_FORMATS = ['none', 'web', 'largest', 'both'] as const
/** Who may OPEN the gallery. Everything downstream asks `access === 'invited'`,
 *  so an unrecognised stored value would read as a public link — the one field
 *  where being permissive means handing a private gallery to the whole web. */
const ACCESS_MODES: readonly ShareAccessMode[] = ['anyone', 'invited']

/**
 * Read a Worker row through exactly one compatibility boundary.
 *
 * `policyVersion === 0` is the ONLY legacy fallback. A malformed/future
 * versioned row is denied instead of being silently widened by old columns.
 * Migration 034 stores originals as one explicit opt-in. The per-photo R2
 * inventory narrows that grant to JPG, RAW, or both when menus are built.
 */
export function adaptStoredSharePolicy(
  row: StoredSharePolicyProjection
): StoredSharePolicyAdapterResult {
  // Checked before the version split: a legacy row stores an access mode too,
  // and neither vintage may be served on a value this build cannot interpret.
  if (!ACCESS_MODES.includes(row.accessMode)) return { ok: false, denial: 'malformed_policy' }
  if (row.policyVersion === 0) {
    const clientMode: ShareClientMode = row.legacyPicksEnabled
      ? 'select'
      : row.legacyDownloadEnabled === 'off'
        ? 'view'
        : 'approve'
    const prepared: readonly PreparedCopy[] =
      row.legacyDownloadEnabled === 'off'
        ? []
        : row.legacyDownloadEnabled === 'web'
          ? ['web']
          : ['largest']
    const originals: readonly OriginalCopy[] =
      row.legacyDownloadEnabled === 'hr' || row.legacyDownloadEnabled === 'original'
        ? ['jpg', 'raw']
        : []
    return {
      ok: true,
      source: 'legacy',
      dto: freezeClientDto({
        policyVersion: 0,
        access: row.accessMode,
        passcodeRequired: row.passcodeRequired,
        can: SHARE_CLIENT_MODE_MATRIX[clientMode],
        downloads: { prepared, originals },
        expiresAt: row.expiresAt,
      }),
    }
  }

  if (row.policyVersion !== 1 && row.policyVersion !== 2)
    return { ok: false, denial: 'unsupported_policy_version' }
  if (
    !CLIENT_MODES.includes(row.clientMode as ShareClientMode) ||
    !PREPARED_FORMATS.includes(row.preparedFormat as (typeof PREPARED_FORMATS)[number])
  ) {
    return { ok: false, denial: 'malformed_policy' }
  }

  const clientMode = row.clientMode as ShareClientMode
  const preparedFormat = row.preparedFormat as (typeof PREPARED_FORMATS)[number]
  const prepared: readonly PreparedCopy[] =
    preparedFormat === 'both'
      ? ['web', 'largest']
      : preparedFormat === 'none'
        ? []
        : [preparedFormat]
  return {
    ok: true,
    source: 'versioned',
    dto: freezeClientDto({
      policyVersion: row.policyVersion,
      access: row.accessMode,
      passcodeRequired: row.passcodeRequired,
      can: shareClientCapabilities(row.policyVersion, clientMode),
      downloads: {
        prepared,
        originals: row.allowOriginals ? ['jpg', 'raw'] : [],
      },
      expiresAt: row.expiresAt,
    }),
  }
}

function freezeClientDto(dto: SharePolicyClientDto): SharePolicyClientDto {
  Object.freeze(dto.downloads.prepared)
  Object.freeze(dto.downloads.originals)
  Object.freeze(dto.downloads)
  return Object.freeze(dto)
}

export type ShareEndpoint =
  | 'manifest'
  | 'photo'
  | 'download_single'
  | 'download_zip'
  | 'events'
  | 'picks'
  | 'comments'
  | 'approve'
  | 'voter_identity'
  | 'notifications_read'
  | 'notifications_write'

export interface EndpointAuthorizationRule {
  reviewerSession: boolean
  ability: ShareClientAbility
  downloadTier: boolean
}

/** Every protected public endpoint and the capability it must re-check. */
export const SHARE_ENDPOINT_AUTHORIZATION: Readonly<
  Record<ShareEndpoint, Readonly<EndpointAuthorizationRule>>
> = Object.freeze({
  manifest: Object.freeze({ reviewerSession: false, ability: 'view', downloadTier: false }),
  photo: Object.freeze({ reviewerSession: false, ability: 'view', downloadTier: false }),
  download_single: Object.freeze({ reviewerSession: false, ability: 'view', downloadTier: true }),
  download_zip: Object.freeze({ reviewerSession: false, ability: 'view', downloadTier: true }),
  events: Object.freeze({ reviewerSession: true, ability: 'view', downloadTier: false }),
  picks: Object.freeze({ reviewerSession: true, ability: 'select', downloadTier: false }),
  comments: Object.freeze({ reviewerSession: true, ability: 'comment', downloadTier: false }),
  approve: Object.freeze({
    reviewerSession: true,
    ability: 'approveDelivery',
    downloadTier: false,
  }),
  voter_identity: Object.freeze({ reviewerSession: true, ability: 'view', downloadTier: false }),
  notifications_read: Object.freeze({
    reviewerSession: true,
    ability: 'view',
    downloadTier: false,
  }),
  notifications_write: Object.freeze({
    reviewerSession: true,
    ability: 'view',
    downloadTier: false,
  }),
})

export type ShareLinkState =
  'active' | 'completed' | 'uploading' | 'paused' | 'revoked' | 'expired' | 'inactive'

export type PasscodeResolution =
  'not_required' | 'missing' | 'incorrect' | 'verified' | 'rate_limited'

export type InvitationResolution =
  'not_required' | 'signed_out' | 'wrong_account' | 'revoked' | 'expired' | 'verified'

export type ReviewerSessionResolution = 'not_required' | 'missing' | 'stale' | 'current'

export type ShareAccessDenialCode =
  | 'not_found'
  | 'access_verification_required'
  | 'wrong_account'
  | 'invitation_revoked'
  | 'invitation_expired'
  | 'passcode_required'
  | 'passcode_incorrect'
  | 'rate_limited'
  | 'revoked'
  | 'paused'
  | 'expired'
  | 'not_ready'
  | 'inactive'
  | 'session_required'
  | 'policy_changed'
  | 'capability_denied'
  | 'tier_not_granted'
  | 'object_unavailable'
  | 'unsupported_policy_version'
  | 'malformed_policy'

export interface ShareAccessDenial {
  code: ShareAccessDenialCode
  httpStatus: 401 | 403 | 404 | 409 | 410 | 423 | 429
  retryable: boolean
  refreshCapabilities: boolean
}

export type ShareAccessResolution =
  { allowed: true; policy: SharePolicyClientDto } | { allowed: false; denial: ShareAccessDenial }

export interface ResolveShareAccessInput {
  endpoint: ShareEndpoint
  policy: SharePolicyClientDto
  linkExists: boolean
  linkState: ShareLinkState
  passcode: PasscodeResolution
  invitation: InvitationResolution
  reviewerSession: ReviewerSessionResolution
}

const DENIALS: Readonly<Record<ShareAccessDenialCode, ShareAccessDenial>> = Object.freeze({
  not_found: { code: 'not_found', httpStatus: 404, retryable: false, refreshCapabilities: false },
  access_verification_required: {
    code: 'access_verification_required',
    httpStatus: 401,
    retryable: true,
    refreshCapabilities: false,
  },
  wrong_account: {
    code: 'wrong_account',
    httpStatus: 403,
    retryable: true,
    refreshCapabilities: false,
  },
  invitation_revoked: {
    code: 'invitation_revoked',
    httpStatus: 410,
    retryable: false,
    refreshCapabilities: false,
  },
  invitation_expired: {
    code: 'invitation_expired',
    httpStatus: 410,
    retryable: true,
    refreshCapabilities: false,
  },
  passcode_required: {
    code: 'passcode_required',
    httpStatus: 401,
    retryable: true,
    refreshCapabilities: false,
  },
  passcode_incorrect: {
    code: 'passcode_incorrect',
    httpStatus: 401,
    retryable: true,
    refreshCapabilities: false,
  },
  rate_limited: {
    code: 'rate_limited',
    httpStatus: 429,
    retryable: true,
    refreshCapabilities: false,
  },
  revoked: { code: 'revoked', httpStatus: 410, retryable: false, refreshCapabilities: false },
  paused: { code: 'paused', httpStatus: 410, retryable: true, refreshCapabilities: false },
  expired: { code: 'expired', httpStatus: 410, retryable: true, refreshCapabilities: false },
  not_ready: {
    code: 'not_ready',
    httpStatus: 423,
    retryable: true,
    refreshCapabilities: false,
  },
  inactive: { code: 'inactive', httpStatus: 410, retryable: false, refreshCapabilities: false },
  session_required: {
    code: 'session_required',
    httpStatus: 401,
    retryable: true,
    refreshCapabilities: true,
  },
  policy_changed: {
    code: 'policy_changed',
    httpStatus: 409,
    retryable: true,
    refreshCapabilities: true,
  },
  capability_denied: {
    code: 'capability_denied',
    httpStatus: 403,
    retryable: false,
    refreshCapabilities: true,
  },
  tier_not_granted: {
    code: 'tier_not_granted',
    httpStatus: 403,
    retryable: false,
    refreshCapabilities: true,
  },
  object_unavailable: {
    code: 'object_unavailable',
    httpStatus: 404,
    retryable: true,
    refreshCapabilities: false,
  },
  unsupported_policy_version: {
    code: 'unsupported_policy_version',
    httpStatus: 409,
    retryable: false,
    refreshCapabilities: true,
  },
  malformed_policy: {
    code: 'malformed_policy',
    httpStatus: 409,
    retryable: false,
    refreshCapabilities: true,
  },
})

function deny(code: ShareAccessDenialCode): ShareAccessResolution {
  return { allowed: false, denial: DENIALS[code] }
}

/**
 * Resolve access before any protected response body or mutation.
 * Credential checks intentionally precede lifecycle checks so a stranger to
 * an invited/passcode gallery cannot use the endpoint to inspect its state.
 */
export function resolveShareAccess(input: ResolveShareAccessInput): ShareAccessResolution {
  if (!input.linkExists) return deny('not_found')

  if (input.policy.access === 'invited') {
    if (input.invitation === 'signed_out' || input.invitation === 'not_required') {
      return deny('access_verification_required')
    }
    if (input.invitation === 'wrong_account') return deny('wrong_account')
    if (input.invitation === 'revoked') return deny('invitation_revoked')
    if (input.invitation === 'expired') return deny('invitation_expired')
  }

  if (input.policy.passcodeRequired) {
    if (input.passcode === 'missing' || input.passcode === 'not_required') {
      return deny('passcode_required')
    }
    if (input.passcode === 'incorrect') return deny('passcode_incorrect')
    if (input.passcode === 'rate_limited') return deny('rate_limited')
  }

  switch (input.linkState) {
    case 'revoked':
      return deny('revoked')
    case 'paused':
      return deny('paused')
    case 'expired':
      return deny('expired')
    case 'uploading':
      return deny('not_ready')
    case 'inactive':
      return deny('inactive')
  }

  const rule = SHARE_ENDPOINT_AUTHORIZATION[input.endpoint]
  if (rule.reviewerSession) {
    if (input.reviewerSession === 'missing' || input.reviewerSession === 'not_required') {
      return deny('session_required')
    }
    if (input.reviewerSession === 'stale') return deny('policy_changed')
  }
  if (!input.policy.can[rule.ability]) return deny('capability_denied')
  return { allowed: true, policy: input.policy }
}

export type ShareDownloadTier = 'web' | 'largest' | 'jpg' | 'raw'

export type DownloadAuthorizationResult =
  | { allowed: true; tier: ShareDownloadTier }
  | { allowed: false; denial: 'tier_not_granted' | 'object_unavailable' }

export function grantedDownloadTiers(policy: SharePolicyClientDto): readonly ShareDownloadTier[] {
  return [...policy.downloads.prepared, ...policy.downloads.originals]
}

/** Authorize one tier; availability may only narrow a policy grant. */
export function authorizeDownloadTier(
  policy: SharePolicyClientDto,
  tier: ShareDownloadTier,
  available: boolean
): DownloadAuthorizationResult {
  if (!grantedDownloadTiers(policy).includes(tier)) {
    return { allowed: false, denial: 'tier_not_granted' }
  }
  if (!available) return { allowed: false, denial: 'object_unavailable' }
  return { allowed: true, tier }
}

/**
 * Material to hash into the reviewer JWT's `policyTag`. It contains no secret,
 * recipient, path, object key, or reviewer identifier. A capability/access/
 * expiry change therefore makes old write sessions stale on their next call.
 */
export function sharePolicyTagMaterial(policy: SharePolicyClientDto): string {
  return JSON.stringify({
    v: policy.policyVersion,
    a: policy.access,
    p: policy.passcodeRequired,
    c: policy.can,
    d: policy.downloads,
    e: policy.expiresAt,
  })
}

/** Server-internal reviewer claims. `vt` and verifiedEmail never enter a DTO. */
export interface ReviewerSessionClaimsV1 {
  version: 1
  kind: 'voter'
  sub: string
  vt: string
  policyTag: string
  access: ShareAccessMode
  verifiedEmail?: string
  iat: number
  exp: number
}

export type ShareAuditSurface = ShareEndpoint | 'access_request' | 'access_verify'
export type ShareAuditOutcome = 'granted' | 'denied' | 'succeeded' | 'failed'

export interface SanitizedShareAccessAuditEvent {
  type: 'share_access'
  surface: ShareAuditSurface
  outcome: ShareAuditOutcome
  policyVersion: number
  access: ShareAccessMode
  denial?: ShareAccessDenialCode
}

export interface SanitizedShareDownloadAuditEvent {
  type: 'share_download'
  surface: 'download_single' | 'download_zip'
  outcome: 'succeeded' | 'failed'
  policyVersion: number
  tier: ShareDownloadTier
  count?: number
  denial?: 'tier_not_granted' | 'object_unavailable'
}

export type SanitizedShareAuditEvent =
  SanitizedShareAccessAuditEvent | SanitizedShareDownloadAuditEvent

export function shareAccessAuditEvent(
  event: SanitizedShareAccessAuditEvent
): SanitizedShareAccessAuditEvent {
  return Object.freeze({ ...event })
}

export function shareDownloadAuditEvent(
  event: SanitizedShareDownloadAuditEvent
): SanitizedShareDownloadAuditEvent {
  const count = event.count === undefined ? undefined : Math.max(0, Math.floor(event.count))
  return Object.freeze({ ...event, ...(count === undefined ? {} : { count }) })
}
